import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency, useCurrencyPrice } from "~/hooks/use-currency-price.hook";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { cartViewedEvent } from "~/tooling/telemetry/events";
import { useCart } from "./cart.context";
import { CartCapacityLimit } from "./cart-capacity-limit.component";
import { CartCheckoutButton } from "./cart-checkout-button.component";
import { EmptyCart } from "./cart-empty.component";
import { CartFooter } from "./cart-footer.component";
import { CartHeader } from "./cart-header.component";
import { CartItemLimit } from "./cart-item-limit.component";
import { CartItems } from "./cart-items.component";
import { CartLimitReached } from "./cart-limit-reached.component";
import { CartProgressBar } from "./cart-progress-bar.component";
import { CartReservationTimer } from "./cart-reservation-timer.component";
import { CartDelivery, CartSummary } from "./cart-vendor.component";
import { CartWrapper } from "./cart-wrapper.component";
import { isOpenCartFromNavigationPending } from "./use-open-cart-from-navigation.hook";

const CART_VIEWED_OPEN_KEY = "telemetry:cart-viewed-open";

let cartViewedOpenClaimedInMemory = false;

/**
 * Claims Cart Viewed once per drawer open. Survives remounts and cartId
 * hydration while open; clears when the drawer closes so the next open can fire.
 * Falls back to in-memory claim when sessionStorage is unavailable or throws.
 */
export function claimCartViewedOncePerOpen(isOpen: boolean): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	if (!isOpen) {
		cartViewedOpenClaimedInMemory = false;
		try {
			sessionStorage.removeItem(CART_VIEWED_OPEN_KEY);
		} catch {
			// ignore sessionStorage failures; in-memory claim already cleared
		}
		return false;
	}
	if (cartViewedOpenClaimedInMemory) {
		return false;
	}
	try {
		if (sessionStorage.getItem(CART_VIEWED_OPEN_KEY)) {
			cartViewedOpenClaimedInMemory = true;
			return false;
		}
		sessionStorage.setItem(CART_VIEWED_OPEN_KEY, "1");
	} catch {
		// sessionStorage unavailable — claim via in-memory flag below
	}
	cartViewedOpenClaimedInMemory = true;
	return true;
}

export function CartSectionDivider() {
	return <hr className="h-4 bg-gray-100 border-0 w-full" />;
}

interface CartProps {
	desktopIconOnly?: boolean;
	mobileIconOnly?: boolean;
	truncateVendorName?: boolean;
	renderDialog?: boolean;
}

export function Cart({
	desktopIconOnly = false,
	mobileIconOnly = false,
	truncateVendorName = false,
	renderDialog = true,
}: CartProps) {
	const {
		cartId,
		pharmacyItems,
		fulfillmentGroups,
		vendor,
		totalPrice,
		remainingCapacity,
		remainingItemsLimit,
		isLoading,
		cartDisclosure,
	} = useCart();

	const hasLimitMessage = remainingItemsLimit <= 0 || remainingCapacity <= 0;

	const { t } = useTranslation("cart");
	const { currencyPrice } = useCurrencyPrice(totalPrice);
	const currency = useCurrency();

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	useEffect(() => {
		if (!pharmacyItems.length && !isOpenCartFromNavigationPending()) {
			cartDisclosure.close();
		}
	}, [pharmacyItems.length]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: once per open, not per remount/length/cartId flicker
	useEffect(() => {
		if (!cartDisclosure.isOpen) {
			claimCartViewedOncePerOpen(false);
			return;
		}
		if (!pharmacyItems.length) {
			return;
		}
		if (!claimCartViewedOncePerOpen(true)) {
			return;
		}
		captureEvent(
			cartViewedEvent(
				cartId,
				pharmacyItems,
				vendor,
				currency,
				fulfillmentGroups,
			),
		);
	}, [cartDisclosure.isOpen, pharmacyItems.length, cartId]);

	return (
		<CartWrapper
			count={pharmacyItems.length}
			vendorName={vendor?.name ?? ""}
			amount={currencyPrice}
			cartDisclosure={cartDisclosure}
			desktopIconOnly={desktopIconOnly}
			mobileIconOnly={mobileIconOnly}
			truncateVendorName={truncateVendorName}
			renderDialog={renderDialog}
		>
			{pharmacyItems.length > 0 ? (
				<>
					<div className="md:px-6">
						<CartHeader
							title={t("yourCart")}
							onCloseButtonClick={cartDisclosure.close}
						/>
					</div>
					<div className="flex flex-col shrink-0" data-testid="cart-content">
						<hr className="hidden md:block border-gray-100" />
						<CartItemLimit />
						<CartCapacityLimit />
						<div
							className={`px-3 md:px-4 pb-3 ${hasLimitMessage ? "pt-3" : "pt-4 md:pt-3"}`}
						>
							<CartProgressBar />
						</div>
					</div>
					<div className="overflow-auto min-h-0 flex-1">
						<div className="h-fit flex flex-col pb-24 md:pb-0">
							<CartItems isLoading={isLoading} />
							<CartFooter />

							<CartSectionDivider />
							<CartDelivery />

							<CartSectionDivider />
							<CartSummary totalPrice={totalPrice} />

							<CartReservationTimer />
						</div>
					</div>
					<div className="flex justify-center flex-col w-full h-24 md:h-fit fixed md:relative bottom-0 left-0 px-4 md:px-6 bg-white z-(--z-index-cart-dialog-content)">
						<CartCheckoutButton />
					</div>
				</>
			) : remainingCapacity <= 0 ? (
				<CartLimitReached />
			) : (
				<EmptyCart />
			)}
		</CartWrapper>
	);
}
